/**
 * Private module reserved for @coursera/cds-core package.
 */

import type React from 'react';

import clsx from 'clsx';

import type { IconProps, ComponentPropsWithoutRef } from '@coursera/cds-common';
import { ErrorIcon, SuccessIcon } from '@coursera/cds-icons';

import { useFormControlContext } from '@core/forms/FormControl/FormControlContext';
import type { ValidationStatus } from '@core/forms/FormControl/types';
import Typography from '@core/Typography2';
import { useValidationAriaLabel } from '@core/utils';
import VisuallyHidden from '@core/VisuallyHidden';

import getFormValidationStatusCss, {
  classes,
} from './getFormValidationStatusCss';

export type Props = {
  /**
   * Label to describe validation state of the form field.
   */
  label: string;

  /**
   * Invert the color scheme. Use when displaying over dark backgrounds
   * @default false
   */
  invert?: boolean;

  /**
   * Determines if the status icon should be rendered
   * @default false
   */
  hideIcon?: boolean;

  /**
   * Defines field success/error styling.
   */
  validationStatus?: ValidationStatus;
} & ComponentPropsWithoutRef<'div'>;

export const getIconToRender = (
  validationStatus: ValidationStatus
): React.ComponentType<IconProps> | null => {
  const iconsMap: Record<
    NonNullable<ValidationStatus>,
    React.ComponentType<IconProps>
  > = {
    error: ErrorIcon,
    success: SuccessIcon,
  };
  return validationStatus ? iconsMap[validationStatus] : null;
};

const FormValidationStatus = (props: Props): React.ReactElement<Props> => {
  const {
    className,
    label,
    hideIcon,
    validationStatus,
    invert,
    ...restProps
  } = props;
  const formControlContext = useFormControlContext({
    id: props.id,
    validationStatus,
    invert,
  });

  const ariaLabel = useValidationAriaLabel(
    label,
    formControlContext.validationStatus
  );

  const IconToRender = getIconToRender(formControlContext.validationStatus);

  return (
    <div
      className={clsx(
        classes.root,
        {
          [classes.invalid]: formControlContext.validationStatus === 'error',
          [classes.valid]: formControlContext.validationStatus === 'success',
          [classes.onDark]: formControlContext.invert,
          [classes.onLight]: !formControlContext.invert,
        },
        className
      )}
      css={getFormValidationStatusCss}
      id={formControlContext.validationLabelId}
      {...restProps}
    >
      {ariaLabel && <VisuallyHidden>{ariaLabel}</VisuallyHidden>}

      {!hideIcon && IconToRender && formControlContext.validationStatus && (
        <IconToRender className={classes.icon} size="medium" />
      )}

      <Typography
        aria-hidden
        color={formControlContext.validationStatus}
        component="span"
        variant="bodySecondary"
      >
        {label}
      </Typography>
    </div>
  );
};

export default FormValidationStatus;
